import { IconCheck } from '@tabler/icons-react'
import { ChevronDown } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

import { cn } from '@/utils'

export function BasicSelectionBox({
  options,
  onValueChange,
  defaultValue,
  resetSelection,
  className,
}: {
  options: { value: string; label: string }[]
  onValueChange: (value: string) => void
  defaultValue?: string
  resetSelection?: React.MutableRefObject<boolean>
  className?: string
}) {
  const [selectedValue, setSelectedValue] = useState(defaultValue || '')
  const [isOpen, setIsOpen] = useState(false)
  const [resetTrigger, setResetTrigger] = useState<number>(0)

  const handleValueChange = useCallback(
    (value: string) => {
      setSelectedValue(value)
      onValueChange(value)
      setIsOpen(false)
    },
    [onValueChange, resetTrigger]
  )

  // Watch for reset trigger from parent component
  useEffect(() => {
    if (resetSelection?.current && options.length > 0) {
      const defaultSelection = defaultValue || ''
      setSelectedValue(defaultSelection)
      resetSelection.current = false
      setResetTrigger((prev) => prev + 1)
      // Notify parent component about the reset
      onValueChange?.(defaultSelection)
    }
  }, [resetSelection, defaultValue, options, onValueChange])

  const getSelectedLabel = useCallback(() => {
    const selectedItem = options.find((item) => item.value === selectedValue)
    return selectedItem?.label || 'Select...'
  }, [selectedValue])

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open)
  }, [])

  return (
    <Popover onOpenChange={handleOpenChange} open={isOpen}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  'w-[120px] justify-start h-8 font-normal',
                  className
                )}
              >
                <span className="truncate">{getSelectedLabel()}</span>
                <ChevronDown className="ml-auto h-4 w-4 text-muted-foreground" />
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-[300px]">
            {getSelectedLabel()}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <PopoverContent className="w-[120px] p-2">
        <div className="max-h-[150px] overflow-y-auto">
          {options.map(({ value, label }, index) => (
            <BaseSelectionBox
              key={`${value}-${index}`}
              title={label}
              isSelected={value === selectedValue}
              onClick={() => handleValueChange(value)}
            />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}

const BaseSelectionBox = ({
  title,
  isSelected,
  isDisabled = false,
  onClick,
}: {
  title: string
  isSelected: boolean
  isDisabled?: boolean
  onClick: () => void
}) => {
  return (
    <div
      className={cn(
        'flex h-10 items-center flex-1 relative cursor-pointer select-none rounded-sm py-1.5 pl-2 pr-7 text-sm outline-none hover:bg-accent hover:text-accent-foreground',
        isDisabled && 'pointer-events-none opacity-50'
      )}
      onClick={() => !isDisabled && onClick()}
    >
      <span className={isSelected ? 'text-blue-600' : ''}>{title}</span>
      <span className="absolute right-2 flex h-5 w-5 items-center justify-center">
        {isSelected && <IconCheck className="text-blue-600" />}
      </span>
    </div>
  )
}
